import { Link } from "react-router-dom";
import { ArrowUpRight, Hash, Smartphone, Settings2, Lock, Apple, Globe2 } from "lucide-react";
import { useLanguage } from "../i18n";
import { usePageMeta } from "../hooks/usePageMeta";
import { Reveal } from "../components/Reveal";

export default function Compatibility() {
  usePageMeta("compat.title", "compat.sub");
  const { t } = useLanguage();

  const guide = [
    [<Hash key="1" className="w-6 h-6" />, t("compat.g1t"), t("compat.g1d")],
    [<Apple key="2" className="w-6 h-6" />, t("compat.g2t"), t("compat.g2d")],
    [<Settings2 key="3" className="w-6 h-6" />, t("compat.g3t"), t("compat.g3d")],
    [<Lock key="4" className="w-6 h-6" />, t("compat.g4t"), t("compat.g4d")],
  ];
  const brands = [
    [t("compat.appleT"), t("compat.appleL")],
    [t("compat.samsungT"), t("compat.samsungL")],
    [t("compat.googleT"), t("compat.googleL")],
    [t("compat.otherT"), t("compat.otherL")],
  ];

  return (
    <div data-testid="compatibility-page" className="px-4 sm:px-6 lg:px-12 max-w-5xl mx-auto py-14 sm:py-20">
      <Reveal>
        <div className="text-center max-w-2xl mx-auto">
          <div className="font-mono-t text-[11px] font-bold uppercase tracking-[0.3em] text-[#FF6B35] mb-4">{t("compat.nav")}</div>
          <h1 className="font-display font-extrabold tracking-tight text-4xl sm:text-6xl text-slate-900 text-balance">{t("compat.title")}</h1>
          <p className="mt-5 text-lg text-slate-600">{t("compat.sub")}</p>
        </div>
      </Reveal>

      <Reveal className="mt-16">
        <h2 className="font-display font-bold text-2xl sm:text-3xl text-slate-900 mb-6">{t("compat.guideTitle")}</h2>
      </Reveal>
      <div className="grid md:grid-cols-2 gap-5">
        {guide.map(([icon, title, text], i) => (
          <Reveal key={title} delay={i * 0.08}>
            <div data-testid={`compat-guide-${i + 1}`} className="rounded-3xl bg-white border border-slate-200/80 shadow-sm p-8 h-full hover:-translate-y-1 hover:shadow-xl hover:shadow-orange-100 transition-all duration-200">
              <div className="flex items-center justify-between mb-6">
                <span className="w-12 h-12 rounded-full bg-[#FF6B35]/10 text-[#FF6B35] flex items-center justify-center">{icon}</span>
                <span className="font-mono-t text-xs font-bold text-slate-300">0{i + 1}</span>
              </div>
              <h3 className="font-display font-bold text-lg text-slate-900 mb-2">{title}</h3>
              <p className="text-sm text-slate-600 leading-relaxed">{text}</p>
            </div>
          </Reveal>
        ))}
      </div>

      <Reveal className="mt-16">
        <div className="rounded-3xl bg-white border border-slate-200/80 shadow-sm p-8 sm:p-12">
          <h2 className="font-display font-bold text-2xl sm:text-3xl text-slate-900 mb-8 flex items-center gap-3">
            <Smartphone className="w-7 h-7 text-[#FF6B35]" />
            {t("compat.title")}
          </h2>
          <div className="space-y-6">
            {brands.map(([brand, list], i) => (
              <div key={brand} data-testid={`compat-brand-${i}`} className="grid sm:grid-cols-[200px_1fr] gap-2 sm:gap-6 pb-6 border-b border-slate-100 last:border-0 last:pb-0">
                <div className="font-mono-t text-xs font-bold uppercase tracking-[0.2em] text-[#FF6B35] pt-1">{brand}</div>
                <p className="text-sm text-slate-600 leading-relaxed">{list}</p>
              </div>
            ))}
          </div>
          <p data-testid="compat-note" className="mt-8 rounded-2xl bg-[#FAF5EE] px-5 py-4 text-sm text-slate-600 leading-relaxed">
            {t("compat.note")}
          </p>
        </div>
      </Reveal>

      <Reveal className="mt-12 text-center">
        <Link to="/destinations" data-testid="compat-cta"
          className="inline-flex items-center gap-2 rounded-full bg-[#FF6B35] text-white px-8 py-3.5 font-mono-t text-xs font-bold uppercase tracking-[0.2em] hover:bg-[#E8551F] transition-colors shadow-lg shadow-orange-200">
          <Globe2 className="w-4 h-4" /> {t("featured.viewAll")} <ArrowUpRight className="w-4 h-4" />
        </Link>
      </Reveal>
    </div>
  );
}
